import React, { FC } from 'react';
import './BirthDateWidget.css';
import { BirthdayCard, Modal, Widget } from '@/Shared/UI/Components';
import { useBirthWishEnricher } from '../../Logic';
import { ActionResponse, ActionStatusHelper, DOMAIN_TYPES, useModal } from '@/Shared';
import { ContactModel } from '@/Domains/Contact';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import GenericDomainForm from '@/Shared/UI/Forms/GenericDomainForm';
import { useBirthWish } from '@/OneLiners';

interface BirthDateWidgetProps { }


/**
 * =====================================================
 * Component: BirthDateWidget
 * =====================================================
 *
 * Description:
 * Displays a widget listing all contacts with birthdays in the current month.
 * Allows creating a new BirthWish for a contact via a modal.
 *
 * Responsibilities:
 * - Fetch and display birthdays for the current month
 * - Render each contact in a BirthdayCard
 * - Open a modal with BirthWishForm when creating a new wish
 * - Integrate with domain-specific widget and modal hooks
 * 
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
const BirthDateWidget: FC<BirthDateWidgetProps> = () => {

    const {dataMap : birthWishMap, stateResponse} = useBirthWish();
	
	// Hook for managing modals
	const modalHook = useModal();

	// Hook for fetching and enriching birthday data
	const { getCurrentMonthBirthdays, enricherResponse } = useBirthWishEnricher();

	const response: ActionResponse = {
		status: ActionStatusHelper.combine(
			stateResponse.status,
			enricherResponse.status
		),
		error: stateResponse.error
			?? enricherResponse.error 
	};

	/**
     * Handles clicking on a contact card
     * Opens a modal to create a new BirthWish
     */
	const handleClick = (contact: ContactModel) => {
		modalHook.openModal({
			title: "Création de Voeu",
			content: <GenericDomainForm
				domainType={DOMAIN_TYPES.BirthWish}
				inModalHook={modalHook} 
				extraArgs={{
					contactId : contact.id
				}}
			/>
		});
	}

	/**
     * Renders the list of BirthdayCards
     */
	const getBirtdayList = () => {
		return <div className='BirthdayCardList sm-scroll'>
			{getCurrentMonthBirthdays(birthWishMap).toArray().map(contact => <BirthdayCard
				key={contact.id}
				name={contact.person.lastname?.toUpperCase() + ' ' + contact.person.firstname}
				birthDate={contact.person.birthDate ?? "0000-00-00"}
				role={contact.contactType}
				onBirthWish={() => handleClick(contact)}
			/>)}
		</div>
	};

	return (
		<>
			<Modal
				className='w-80 minH-80'
				onClose={modalHook.closeModal}
				data={modalHook.modalData}
			/>
			<Widget
				domainLabel={DOMAIN_CONFIGS.BirthWish.domainLabel}
				response={response}
				title='Anniversaires du mois'
				content={getBirtdayList()} />
		</>
	);
}

export default BirthDateWidget;
